import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CurrentDashboardProfileContext, DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { isAgency, isFinanceVertical, isIndividual, isTeam } from '@rma/generic/domain/helpers';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { cache } from 'decorator-cache-getter';
import { distinctUntilChanged, map, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { NavigationGroup } from '../entities/side-nav-items';
import { IndividualFinanceSideMenuService } from './individual-finance-side-menu.service';
import { IndividualSalesSideMenuService } from './individual-sales-side-menu.service';
import { OfficeSideMenuService } from './office-side-menu.service';
import { TeamSideMenuService } from './team-side-menu.service';

@Injectable({
  providedIn: 'root',
})
export class SideNavigationService {
  private readonly location = inject(Location);
  private readonly profileContextService = inject(DashboardProfileContextService);

  private readonly individualFinanceMenuService = inject(IndividualFinanceSideMenuService);
  private readonly individualSalesRoutesService = inject(IndividualSalesSideMenuService);
  private readonly officeRoutesService = inject(OfficeSideMenuService);
  private readonly teamRoutesService = inject(TeamSideMenuService);

  public currentUrl$: Observable<string>;

  private currentUrlSource = new Subject<string>();

  constructor() {
    //this can probably be replace with RouteUtilityService
    this.currentUrl$ = this.currentUrlSource.asObservable().pipe(startWith(this.location.path() || '/'), distinctUntilChanged());
    this.location.onUrlChange((url) => this.currentUrlSource.next(url.replace('/dashboard', '')));
  }

  @cache
  public get navigation$(): Observable<NavigationGroup[]> {
    return this.profileContextService.profileContext$.pipe(
      nonNullable(),
      switchMap((x) => this.mapNavigation(x)),
      map((n) => n.filter((g) => g.items.length > 0)),
      rmaShareReplay(),
    );
  }

  private mapNavigation(profile: CurrentDashboardProfileContext): Observable<NavigationGroup[]> {
    if (isFinanceVertical(profile.primaryVerticalType)) {
      return this.individualFinanceMenuService.getNavigation();
    } else {
      if (isAgency(profile)) {
        if (isTeam(profile)) {
          return this.teamRoutesService.getNavigation();
        }

        return this.officeRoutesService.getNavigation();
      }

      if (isIndividual(profile)) {
        return this.individualSalesRoutesService.getNavigation();
      }
    }

    return of([]);
  }
}
